import { useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import {
  addManualSubBasin,
  deleteSubBasin,
  parseSubBasinsCsv,
  subBasinsToCsv,
  type SubBasinColumnMapping,
} from "../../model/projectEntities.ts";
import type { MapMitecoMode } from "../../model/mitecoLayers.ts";
import type { DrawingTool } from "../drawingTools.ts";
import { ConfirmDeleteDialog } from "../ConfirmDeleteDialog.tsx";
import { EditIcon } from "../EditIcon.tsx";
import { FileButton } from "../FileButton.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { TrashIcon } from "../TrashIcon.tsx";
import { DrawButton } from "./DrawButton.tsx";
import { EditSubBasinDialog } from "./EditSubBasinDialog.tsx";
import { GisLayerPanel } from "./GisLayerPanel.tsx";
import { useProjectEditing } from "./hooks.ts";

/**
 * Sub-basin management: multi-selection on the map/canvas (click,
 * Shift+drag adds, Ctrl+drag removes — see `selection`/SubBasinMap.tsx) up
 * top, to bulk-delete several at once; the full catalog (manual entry,
 * name/area editing, individual deletion) below. Assigning zone/outlet/
 * aquifer to a selection lives in each of THOSE tabs instead (Zonas/Puntos
 * de desagüe/Acuíferos, each with its own assignment block) — this tab
 * used to also have a bulk field-assigner here, redundant with those and
 * removed in favor of them; the selection block used to live in
 * EditBar.tsx too, above even the tab bar — now it's here, below this
 * tab's own title, the only one it applies to.
 */
export function SubBasinsPanel({
  onRecoverFromGis,
  selection,
  onChangeSelection,
  mitecoMode,
  onToggleMitecoMode,
  onChangeMitecoLayer,
  onAddSelectedMiteco,
  drawingMode,
  onStartDrawing,
  onCancelDrawing,
}: {
  onRecoverFromGis: (geojson: GeoJSON.FeatureCollection, mapping: SubBasinColumnMapping) => void;
  selection: Set<number>;
  onChangeSelection: (selection: Set<number>) => void;
  /** "Add from MITECO" mode — undefined: off (button unpressed). See mitecoLayers.ts. */
  mitecoMode: MapMitecoMode | undefined;
  onToggleMitecoMode: () => void;
  onChangeMitecoLayer: (layer: "basins" | "subBasins") => void;
  onAddSelectedMiteco: () => void;
  drawingMode: DrawingTool | null;
  onStartDrawing: (tool: DrawingTool) => void;
  onCancelDrawing: () => void;
}) {
  const t = useT();
  const {
    project,
    onChangeProject: onChange,
    gisLayer,
    onChangeGisLayer,
    series,
    onChangeSeries,
    aemetProvenance,
    onRecordAemetProvenance,
  } = useProjectEditing();
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvNotice, setCsvNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);

  const editingSubBasin = project.subBasins.find((c) => c.subBasinCode === editing);
  const deletingSubBasin = project.subBasins.find((c) => c.subBasinCode === deleting);
  const selected = project.subBasins.filter((c) => selection.has(c.subBasinCode));

  function add() {
    setManualError(null);
    try {
      onChange(addManualSubBasin(project, name, Number(area) || 0));
      setName("");
      setArea("");
    } catch (err) {
      setManualError(translateError(err, t));
    }
  }

  async function onCsv(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setCsvError(null);
    setCsvNotice(null);
    try {
      const parsed = parseSubBasinsCsv(await file.text());
      let updated = project;
      for (const entry of parsed) updated = addManualSubBasin(updated, entry.name, entry.area);
      onChange(updated);
      setCsvNotice(t("step1.csvImported", { n: parsed.length }));
    } catch (err) {
      setCsvError(translateError(err, t));
    }
  }

  /** Mirrors the wizard's CSV import format (projectEntities.ts's parseSubBasinsCsv/subBasinsToCsv) — mainly meant as a quick reference for the exact sub-basin names to use when building the precipitation/ETP CSV by hand (they're linked by name, see PasteDailySeries.tsx). */
  function exportCsv() {
    const blob = new Blob([subBasinsToCsv(project)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "subcuencas.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function clearSelection() {
    onChangeSelection(new Set());
  }

  // Same toggle logic as clicking a sub-basin on the map/canvas in this same
  // tab (see selectionRef's click handler in SubBasinMap.tsx) — a plain
  // click here just (de)selects; double-click opens the edit dialog
  // directly, same as double-clicking the polygon on the map itself.
  function toggleSelection(code: number) {
    const copy = new Set(selection);
    if (copy.has(code)) copy.delete(code);
    else copy.add(code);
    onChangeSelection(copy);
  }

  return (
    <div>
      <details className="collapsible-section">
        <summary>{t("subBasinsPanel.createManualTitle")}</summary>
        <div className="step-form">
          <input
            placeholder={t("common.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            placeholder={t("resultsPanel.areaKm2")}
            type="number"
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
          <button className="button" onClick={add} disabled={!name.trim()}>
            {t("subBasinsPanel.addManual")}
          </button>
        </div>
        {manualError && <p className="error">{manualError}</p>}
      </details>

      <details className="collapsible-section">
        <summary>{t("subBasinsPanel.drawTitle")}</summary>
        <DrawButton
          tool="subBasin"
          drawingMode={drawingMode}
          onStartDrawing={onStartDrawing}
          onCancelDrawing={onCancelDrawing}
        />
      </details>

      <details className="collapsible-section">
        <summary>{t("subBasinsPanel.addFromMiteco")}</summary>
        <div className="draw-buttons-panel">
          <button
            className={mitecoMode ? "button button-primary" : "button"}
            onClick={onToggleMitecoMode}
          >
            {t("subBasinsPanel.addFromMiteco")}
          </button>
          {mitecoMode && (
            <button className="button" onClick={onToggleMitecoMode}>
              {t("common.cancel")}
            </button>
          )}
        </div>

        {mitecoMode && (
          <div className="miteco-add-panel">
            <div className="edit-panel-tabs">
              <button
                className={mitecoMode.layer === "basins" ? "tab-active" : undefined}
                onClick={() => onChangeMitecoLayer("basins")}
              >
                {t("subBasinsPanel.mitecoBasinsLayer")}
              </button>
              <button
                className={mitecoMode.layer === "subBasins" ? "tab-active" : undefined}
                onClick={() => onChangeMitecoLayer("subBasins")}
              >
                {t("subBasinsPanel.mitecoSubBasinsLayer")}
              </button>
            </div>
            <p className="muted-text">{t("subBasinsPanel.mitecoHelp")}</p>

            {mitecoMode.selected.size > 0 && (
              <div className="edit-bar-selection">
                <div className="edit-bar-selection-header">
                  <strong>
                    {t(
                      mitecoMode.selected.size > 1
                        ? "subBasinsPanel.mitecoMultipleSelected"
                        : "subBasinsPanel.mitecoOneSelected",
                      { n: mitecoMode.selected.size }
                    )}
                  </strong>
                  <div className="edit-bar-selection-actions">
                    <button
                      className="button-icon"
                      title={t("subBasinsPanel.clearSelection")}
                      onClick={() => mitecoMode.onChangeSelected(new Map())}
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <button className="button button-primary" onClick={onAddSelectedMiteco}>
                  {t("subBasinsPanel.addMitecoSelection")}
                </button>
              </div>
            )}
          </div>
        )}
      </details>

      <details className="collapsible-section">
        <summary>{t("subBasinsPanel.linkGisLayer")}</summary>
        <GisLayerPanel
          subBasins={project.subBasins}
          gisLayer={gisLayer}
          onLinked={onChangeGisLayer}
          onRecover={onRecoverFromGis}
        />
      </details>

      <details className="collapsible-section">
        <summary>{t("subBasinsPanel.csvTitle")}</summary>
        <div className="draw-buttons-panel">
          <FileButton accept=".csv" onFiles={onCsv} label={t("step1.uploadCsvButton")} />
          <InfoIcon text={t("step1.csvHelp")} />
        </div>
        {csvError && <p className="error">{csvError}</p>}
        {csvNotice && <p className="engine-notice">{csvNotice}</p>}
        <button className="button" onClick={exportCsv} disabled={project.subBasins.length === 0}>
          {t("subBasinsPanel.exportCsv")}
        </button>
      </details>

      {selection.size > 0 && (
        <div className="edit-bar-selection">
          <div className="edit-bar-selection-header">
            <strong>
              {t(
                selection.size > 1
                  ? "subBasinsPanel.multipleSelected"
                  : "subBasinsPanel.oneSelected",
                { n: selection.size }
              )}
            </strong>
            <div className="edit-bar-selection-actions">
              <button
                className="button-icon button-danger"
                title={t("subBasinsPanel.deleteSelected")}
                onClick={() => setConfirmingBulkDelete(true)}
              >
                <TrashIcon />
              </button>
              <button
                className="button-icon"
                title={t("subBasinsPanel.clearSelection")}
                onClick={clearSelection}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <h4>{t("subBasinsPanel.catalog")}</h4>
      {project.subBasins.length === 0 ? (
        <p className="engine-notice">{t("canvas.noProjectSubBasins")}</p>
      ) : (
        <>
          <div className="table-scroll catalog-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("common.name")}</th>
                  <th>{t("resultsPanel.areaKm2")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {project.subBasins.map((c) => (
                  <tr
                    key={c.subBasinCode}
                    className={selection.has(c.subBasinCode) ? "row-selected" : undefined}
                    onClick={() => toggleSelection(c.subBasinCode)}
                    onDoubleClick={() => setEditing(c.subBasinCode)}
                  >
                    <td>
                      {c.description}
                      {c.gisCode == null && t("subBasinsPanel.noGis")}
                    </td>
                    <td>{c.area.toFixed(1)}</td>
                    <td>
                      <div className="catalog-row-actions">
                        <button
                          className="button-icon button-icon-hover-reveal"
                          title={t("subBasinsPanel.editTitle")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(c.subBasinCode);
                          }}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="button-icon button-danger"
                          title={t("subBasinsPanel.deletePermanentTitle")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(c.subBasinCode);
                          }}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editingSubBasin && (
        <EditSubBasinDialog
          project={project}
          subBasin={editingSubBasin}
          series={series}
          gisLayer={gisLayer}
          aemetProvenance={aemetProvenance[editingSubBasin.subBasinCode]}
          onRecordAemetProvenance={onRecordAemetProvenance}
          onChangeProject={onChange}
          onChangeSeries={onChangeSeries}
          onClose={() => setEditing(null)}
        />
      )}

      {deletingSubBasin && (
        <ConfirmDeleteDialog
          title={t("subBasinsPanel.deleteDialogTitle")}
          name={deletingSubBasin.description}
          onConfirm={() => onChange(deleteSubBasin(project, deletingSubBasin.subBasinCode))}
          onClose={() => setDeleting(null)}
        />
      )}

      {confirmingBulkDelete && (
        <ConfirmDeleteDialog
          title={t("subBasinsPanel.deleteDialogPluralTitle")}
          name={selected.map((c) => c.description).join(", ")}
          onConfirm={() => {
            let p = project;
            for (const c of selected) p = deleteSubBasin(p, c.subBasinCode);
            onChange(p);
            clearSelection();
          }}
          onClose={() => setConfirmingBulkDelete(false)}
        />
      )}
    </div>
  );
}
